import React, { useMemo } from "react";
import { flatMap } from "lodash";
import styled from "styled-components";
import DateFromNow from "@backpacker/date/from-now";
import { localize } from "@backpacker/number";
import PictureWall from "@charty/picture-wall";
import { FontWeight, ellipsis } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import Icon from "@ui-kit/icon";
import { getSinaweiboImageUrl } from "@radarly/api/post";
import { tr } from "@radarly/i18n";
import { getCoverFallback } from "@radarly/models/picture";
import { Post, PostLensFactory, isTikTokPost } from "@radarly/models/posts";
import { Project } from "@radarly/models/project";

const getProxiedUrl = (project, url) =>
  url && url.includes(".sinaimg.cn") ? getSinaweiboImageUrl(project, url) : url;

export interface IProps {
  posts: Post[];
  project: Project;
  locale: Locale;
  timezone: Timezone;
  className?: string;
  onClick?: () => void;
}

const TooltipHeader = styled.div`
  margin-bottom: 6px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const TooltipAuthor = styled.div`
  ${ellipsis()};
  max-width: 110px;
  margin-right: 5px;
`;

const StyledIcon = styled(Icon)`
  margin-right: 5px;
`;

const TooltipMetric = styled.div`
  font-weight: ${FontWeight.Normal};
  text-align: left;
  font-size: 12px;
`;

const tooltipScore = (score: number, locale: Locale): string =>
  !score ? "N/A" : localize(locale, score);

const RadarlyWidgetPictureWall = ({
  posts,
  project,
  locale,
  timezone,
  className,
  onClick,
}: IProps) => {
  const items = useMemo(() => {
    return flatMap(posts, (post) => {
      const lens = PostLensFactory.create(post);
      if (!lens.hasMedia()) {
        return [];
      }
      const imageFallback = getCoverFallback(lens, project);
      const permalink = lens.getPermalink();

      const attachments: {
        id: string;
        src: string;
        mediaFormat: "picture" | "video";
        srcFallback: string;
        href: string;
        data: Post;
      }[] = [];
      const mainImage = getProxiedUrl(project.id, post.media.image[0]);
      const mainVideo = post.media.video[0]
        ? getProxiedUrl(project.id, post.media.video[0].thumbnail)
        : null;

      if (mainImage) {
        attachments.push({
          id: post.uid,
          src: mainImage || "",
          mediaFormat: "picture",
          srcFallback: imageFallback,
          href: permalink,
          data: post,
        });
      }
      if (mainVideo) {
        attachments.push({
          id: post.uid,
          src: isTikTokPost(post) ? mainVideo : mainVideo.thumbnail,
          mediaFormat: "video",
          srcFallback: imageFallback,
          href: permalink,
          data: post,
        });
      }

      return attachments;
    });
  }, [posts, project]);

  return (
    <PictureWall
      items={items}
      locale={locale}
      timezone={timezone}
      className={className}
      buttonLabel={tr(locale, "POST::RELATED")}
      onClick={onClick}
      renderTooltip={(data: Post) => {
        const lens = PostLensFactory.create(data);
        const author = lens.getAuthorName();
        return (
          <>
            <TooltipHeader>
              <StyledIcon glyph={data.origin.platform} />
              <TooltipAuthor>{author}</TooltipAuthor> -{" "}
              <DateFromNow
                date={data.date}
                locale={locale}
                timezone={timezone}
              />
            </TooltipHeader>
            <TooltipMetric>
              {tr(locale, `POST::METRIC::ENGAGEMENT`)}:{" "}
              {tooltipScore(data.engagementScore, locale)}
            </TooltipMetric>
            <TooltipMetric>
              {tr(locale, `POST::METRIC::REACH`)}:{" "}
              {tooltipScore(data.reach, locale)}
            </TooltipMetric>
          </>
        );
      }}
    />
  );
};

export default RadarlyWidgetPictureWall;
